import { FontStore } from '../font-store.ts';
import { createImageLoader } from '../image-loader.ts';
import { layoutPages } from '../layout/layout.ts';
import type { MakerCtx } from '../maker-ctx.ts';
import { readDocumentDefinition } from '../read/read-document.ts';
import { renderDocument } from '../render/render-document.ts';
import { readAs } from '../util/types.ts';
import type { DocumentDefinition } from './document.ts';
import type { FontStyle, FontWeight } from './text.ts';

export type FontConfig = {
  family?: string;
  style?: FontStyle;
  weight?: FontWeight;
};

/**
 * Generates PDF documents.
 */
export class PdfMaker {
  #fontStore: FontStore;
  #resourceRoot?: string;

  constructor() {
    this.#fontStore = new FontStore();
  }

  /**
   * Registers a font to be used in generated PDFs.
   *
   * @param data The font data. Must be in OpenType (OTF) or TrueType
   * (TTF) format.
   * @param config Additional configuration of the font, only needed if
   * the meta data cannot be extracted from the font.
   */
  registerFont(data: Uint8Array, config?: FontConfig): void {
    this.#fontStore.registerFont(data, config);
  }

  /**
   * Sets the root directory to read resources from. This allows using
   * `file:/` URLs with relative paths in the document definition.
   *
   * @param root The root directory to read resources from.
   */
  setResourceRoot(root: string): void {
    this.#resourceRoot = root;
  }

  /**
   * Generates a PDF from the given document definition.
   *
   * @param definition The definition of the document to generate.
   * @returns The generated PDF document.
   */
  async makePdf(definition: DocumentDefinition): Promise<Uint8Array> {
    const def = readAs(definition, 'definition', readDocumentDefinition);
    const ctx: MakerCtx = {
      fontStore: this.#fontStore,
      imageLoader: createImageLoader(this.#resourceRoot),
    };
    if (def.dev?.guides != null) ctx.guides = def.dev.guides;
    const pages = await layoutPages(def, ctx);
    return await renderDocument(def, pages);
  }
}
